import { COMPANY_CURRENCIES } from '@gokrew/models/company';
import * as Yup from 'yup';
import type { TranslationNS } from '@gokrew/models/constants';
import type { TFunction } from 'i18next';
import {
  emailValidator,
  nameValidator,
  nonWhiteSpaceValidator,
  specialCharValidator,
  strictNoSpaceValidator,
  urlValidator,
} from '@gokrew/ui/utility/validators';

//  Login schema
export const loginSchema = (t: TFunction<TranslationNS.Auth>) =>
  Yup.object({
    email: emailValidator(t('validation.login.email_invalid')).required(
      t('validation.login.email_required')
    ),
    password: Yup.string().required(t('validation.login.password_required')),
    remember: Yup.boolean().optional().default(false), // not required, but keep for type safety
  });

// Signup schema
export const signupSchema = (t: TFunction<TranslationNS.Auth>) =>
  Yup.object({
    firstName: nameValidator()
      .concat(
        nonWhiteSpaceValidator(t('validation.signup.firstName.whitespace'))
      )
      .min(2, t('validation.signup.firstName.min'))
      .max(40, t('validation.signup.firstName.max'))
      .required(t('validation.signup.firstName.required')),
    lastName: nameValidator()
      .concat(
        nonWhiteSpaceValidator(t('validation.signup.lastName.whitespace'))
      )
      .min(2, t('validation.signup.lastName.min'))
      .max(40, t('validation.signup.lastName.max'))
      .required(t('validation.signup.lastName.required')),
    email: emailValidator(t('validation.signup.email.invalid'))
      .concat(strictNoSpaceValidator(t('validation.signup.email.space')))
      .matches(/^\S*$/, t('validation.signup.email.space'))
      .required(t('validation.signup.email.required')),
    password: strictNoSpaceValidator(t('validation.signup.password.spaces'))
      .min(8, t('validation.signup.password.min'))
      .max(40, t('validation.signup.password.max'))
      .matches(/[A-Z]/, t('validation.signup.password.uppercase'))
      .matches(/[0-9]/, t('validation.signup.password.number'))
      .concat(specialCharValidator(t('validation.signup.password.special')))
      .matches(/^\S*$/, t('validation.signup.password.spaces'))
      .required(t('validation.signup.password.required')),
    confirmPassword: strictNoSpaceValidator(
      t('validation.signup.password.spaces')
    )
      .oneOf(
        [Yup.ref('password')],
        t('validation.signup.confirm_password.match')
      )
      .matches(/^\S*$/, t('validation.signup.password.spaces'))
      .required(t('validation.signup.confirm_password.required')),
  });

//  Forgot password schema
export const forgotPasswordSchema = (t: TFunction<TranslationNS.Auth>) =>
  Yup.object({
    email: emailValidator().required(t('validation.signup.email.required')),
  });

//  Reset password schema
export const resetPasswordSchema = (t: TFunction<TranslationNS.Auth>) =>
  Yup.object({
    newPassword: Yup.string()
      .min(8, t('validation.signup.password.min'))
      .matches(/[A-Z]/, t('validation.signup.password.uppercase'))
      .matches(/[0-9]/, t('validation.signup.password.number'))
      .concat(specialCharValidator(t('validation.signup.password.special')))
      .matches(/^\S*$/, t('validation.signup.password.spaces'))
      .required(t('validation.signup.password.required')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], t('validation.reset_password.match'))
      .required(t('validation.reset_password.required')),
  });

export const companySchema = (t: TFunction<TranslationNS.Auth>) =>
  Yup.object({
    currency: Yup.string()
      .oneOf([...COMPANY_CURRENCIES], t('validation.company.currency_required'))
      .required(t('validation.company.currency_required')),
    legalName: Yup.string()
      .min(2, t('validation.company.legal_name_min'))
      .max(40, t('validation.company.legal_name_max'))
      .required(t('validation.company.legal_name_required')),
    abbreviatedName: Yup.string()
      .min(2, t('validation.company.abbreviated_name_min'))
      .max(40, t('validation.company.abbreviated_name_max')),
    websiteUrl: urlValidator(t('validation.company.website_invalid'))
      .trim()
      .required(t('validation.company.website_required')),
    language: Yup.string().required(t('validation.company.language_required')),
  });

// Signup details schema (auth-v2 step 3 — name + password, no email)
export const signupDetailsSchema = (t: TFunction<TranslationNS.Auth>) =>
  Yup.object({
    firstName: nameValidator()
      .concat(
        nonWhiteSpaceValidator(t('validation.signup.firstName.whitespace'))
      )
      .min(2, t('validation.signup.firstName.min'))
      .max(40, t('validation.signup.firstName.max'))
      .required(t('validation.signup.firstName.required')),
    lastName: nameValidator()
      .concat(
        nonWhiteSpaceValidator(t('validation.signup.lastName.whitespace'))
      )
      .min(2, t('validation.signup.lastName.min'))
      .max(40, t('validation.signup.lastName.max'))
      .required(t('validation.signup.lastName.required')),
    password: strictNoSpaceValidator(t('validation.signup.password.spaces'))
      .min(8, t('validation.signup.password.min'))
      .max(40, t('validation.signup.password.max'))
      .matches(/[A-Z]/, t('validation.signup.password.uppercase'))
      .matches(/[0-9]/, t('validation.signup.password.number'))
      .concat(specialCharValidator(t('validation.signup.password.special')))
      .matches(/^\S*$/, t('validation.signup.password.spaces'))
      .required(t('validation.signup.password.required')),
    confirmPassword: strictNoSpaceValidator(
      t('validation.signup.password.spaces')
    )
      .oneOf(
        [Yup.ref('password')],
        t('validation.signup.confirm_password.match')
      )
      .matches(/^\S*$/, t('validation.signup.password.spaces'))
      .required(t('validation.signup.confirm_password.required')),
  });

//Change password schema
export const changePasswordSchema = (t: TFunction<TranslationNS.Auth>) =>
  Yup.object({
    currentPassword: Yup.string()
      .min(8, t('validation.signup.password.min'))
      .matches(/[A-Z]/, t('validation.signup.password.uppercase'))
      .matches(/[0-9]/, t('validation.signup.password.number'))
      .concat(specialCharValidator(t('validation.signup.password.special')))
      .matches(/^\S*$/, t('validation.signup.password.spaces'))
      .required(t('validation.reset_password.current_required')),
    newPassword: Yup.string()
      .min(8, t('validation.signup.password.min'))
      .matches(/[A-Z]/, t('validation.signup.password.uppercase'))
      .matches(/[0-9]/, t('validation.signup.password.number'))
      .concat(specialCharValidator(t('validation.signup.password.special')))
      .matches(/^\S*$/, t('validation.signup.password.spaces'))
      .required(t('validation.reset_password.new_required')),
    confirmNewPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], t('validation.reset_password.match'))
      .required(t('validation.reset_password.confirm_new_required')),
  });
